// src/config/gameRegistry.js
//
// ===========================================================================
// THE ONE PLACE A GAME OR A BONUS ROUND IS DECLARED
// ===========================================================================
// Before this file the catalogue of playable things was written out four
// times, in four different vocabularies:
//
//   games/UniversalQuizGame.js       switch (gameType) { case "multiplechoice" ...
//   components/quizzes/BonusRouter.js switch (normalizedBonusType) { case "memorymatch" ...
//   pages/admin/GameLab.js            MAIN_GAMES / BONUS_ROUNDS arrays
//   pages/builder/TopicLibraryPanel.js GAME_TYPES / BONUS_TYPES dropdown arrays
//
// So adding a game meant remembering four edits, and forgetting the fourth
// meant the game worked but could not be selected on a topic. This file is
// the list. GameLibraryPanel and TopicLibraryPanel both read it, so a game
// added here is immediately playable in the builder AND selectable on a
// topic, with no second edit.
//
// ---------------------------------------------------------------------------
// HOW TO ADD A NEW MAIN GAME
// ---------------------------------------------------------------------------
//   1. Build the component in src/components/quizzes/.
//   2. Add one entry to MAIN_GAMES below.
//   3. Add the matching `case` to the switch in games/UniversalQuizGame.js,
//      so it also runs for real students, not just in the builder.
//
// HOW TO ADD A NEW BONUS ROUND
// ---------------------------------------------------------------------------
//   1. Build the component in src/components/quizzes/.
//   2. Add one entry to BONUS_ROUNDS below.
//   3. Add the matching `case` to the switch in
//      components/quizzes/BonusRouter.js.
//
// Step 3 is the one that cannot live here: this file is deliberately free of
// component imports so that anything reading the dropdown lists — the Topic
// Library panel, for one — does not drag every game and every game
// stylesheet into its bundle. Components are reached through `load`, a
// dynamic import that only fires when something actually plays the game.
//
// ---------------------------------------------------------------------------
// FIELDS
// ---------------------------------------------------------------------------
//   id          Stable key for this catalogue entry. Not the same as
//               gameType: three entries share the gameType "diamondTap" and
//               differ only by slug.
//   label       What a human sees in the picker.
//   gameType /  The literal string a topic carries. Whatever you put here is
//   bonusRound  what gets pasted into a topic, so spell it the way a topic
//   Type        would.
//   sample      Which key of SAMPLES this thing can actually read. Getting
//               this wrong is the single most common reason a game looks
//               broken in a harness — see the note on SAMPLES below.
//   load        () => import(...). Main games only; bonus rounds are reached
//               through BonusRouter, which does its own importing.
//   slug        Bonus rounds only, and only for the diamondTap family, whose
//               component is chosen by SLUG rather than by type.
//   note        Anything known to be odd about this entry, shown under the
//               picker so it is read at the moment it matters.
// ===========================================================================

/* ------------------------------------------------------------------ */
/* Sample content                                                      */
/* ------------------------------------------------------------------ */

// Deliberately dull and self-evidently fake. Sample content that reads like
// real curriculum gets copied into a real topic eventually.
//
// These are not interchangeable. MultipleChoice reads `current.answers`,
// MatchingQuiz reads `current.pairs`, DragDropQuiz reads `current.options`
// with targetText, CountPop reads questions[0] as a SETTINGS OBJECT rather
// than a question, and MatchThree / PuzzleShuffle / RhythmTap build their own
// boards and barely look at questions at all. One array cannot satisfy all of
// them, which is why every entry declares which shape it needs.
export const SAMPLES = {
  // MultipleChoice, TalkChoice, TypingQuiz
  answers: [
    {
      question: "Sample 1 — which answer is marked correct?",
      answers: [
        { text: "Correct answer", correct: true },
        { text: "Wrong answer A", correct: false },
        { text: "Wrong answer B", correct: false },
        { text: "Wrong answer C", correct: false },
      ],
    },
    {
      question: "Sample 2 — pick the right one.",
      answers: [
        { text: "Wrong answer A", correct: false },
        { text: "Correct answer", correct: true },
        { text: "Wrong answer B", correct: false },
        { text: "Wrong answer C", correct: false },
      ],
    },
    {
      question:
        "Sample 3 — a longer prompt, to check how the card wraps when the question runs past a single line of text.",
      answers: [
        { text: "Wrong answer A", correct: false },
        { text: "Wrong answer B", correct: false },
        { text: "Correct answer", correct: true },
        { text: "Wrong answer C", correct: false },
      ],
    },
    {
      question: "Sample 4 — two answers only.",
      answers: [
        { text: "Correct answer", correct: true },
        { text: "Wrong answer A", correct: false },
      ],
    },
    {
      question: "Sample 5 — last one.",
      answers: [
        { text: "Wrong answer A", correct: false },
        { text: "Wrong answer B", correct: false },
        { text: "Wrong answer C", correct: false },
        { text: "Correct answer", correct: true },
      ],
    },
  ],

  // DragDropQuiz, MemoryMatchGame
  //
  // EVERY OPTION MUST CARRY AN `id`. This is not cosmetic.
  // DragDropQuiz identifies a dragged item solely by item.id — it puts
  // { id } on the dataTransfer, keys `matches` by it, and hides an item that
  // has already been placed with:
  //
  //     .filter(item => !Object.values(matches).includes(item.id))
  //
  // With no id on the data, every item's id is undefined, so dropping ONE
  // item writes undefined into matches and that filter then matches every
  // remaining item at once — the entire tray vanishes on the first drop.
  // Real topic data always carries an id (see the nc-topics quiz content
  // files), so this shape has to as well or the harness tests something no
  // student will ever encounter.
  options: [
    {
      question: "Sample 1 — drag each item onto the group it belongs with.",
      options: [
        { id: "opt1", text: "Item A1", targetText: "Group A" },
        { id: "opt2", text: "Item B1", targetText: "Group B" },
        { id: "opt3", text: "Item A2", targetText: "Group A" },
        { id: "opt4", text: "Item B2", targetText: "Group B" },
      ],
    },
    {
      question: "Sample 2 — three groups this time.",
      options: [
        { id: "opt5", text: "Item A", targetText: "Group A" },
        { id: "opt6", text: "Item B", targetText: "Group B" },
        { id: "opt7", text: "Item C", targetText: "Group C" },
      ],
    },
    {
      question: "Sample 3 — six items across two groups.",
      options: [
        { id: "opt8", text: "Item A1", targetText: "Group A" },
        { id: "opt9", text: "Item A2", targetText: "Group A" },
        { id: "opt10", text: "Item A3", targetText: "Group A" },
        { id: "opt11", text: "Item B1", targetText: "Group B" },
        { id: "opt12", text: "Item B2", targetText: "Group B" },
        { id: "opt13", text: "Item B3", targetText: "Group B" },
      ],
    },
    {
      question: "Sample 4 — one item per group.",
      options: [
        { id: "opt14", text: "Only item A", targetText: "Group A" },
        { id: "opt15", text: "Only item B", targetText: "Group B" },
      ],
    },
    {
      question: "Sample 5 — last one.",
      options: [
        { id: "opt16", text: "Item A", targetText: "Group A" },
        { id: "opt17", text: "Item B", targetText: "Group B" },
        { id: "opt18", text: "Item C", targetText: "Group C" },
        { id: "opt19", text: "Item D", targetText: "Group A" },
      ],
    },
  ],

  // MatchingQuiz reads current.pairs with {left, right}.
  pairs: [
    {
      prompt: "Sample 1 — match each left item to its partner.",
      pairs: [
        { left: "Left one", right: "Right one" },
        { left: "Left two", right: "Right two" },
        { left: "Left three", right: "Right three" },
      ],
    },
    {
      prompt: "Sample 2 — four pairs.",
      pairs: [
        { left: "Alpha", right: "First" },
        { left: "Beta", right: "Second" },
        { left: "Gamma", right: "Third" },
        { left: "Delta", right: "Fourth" },
      ],
    },
    {
      prompt: "Sample 3 — two pairs only.",
      pairs: [
        { left: "Left one", right: "Right one" },
        { left: "Left two", right: "Right two" },
      ],
    },
    {
      prompt: "Sample 4 — longer labels, to check truncation.",
      pairs: [
        {
          left: "A left label that runs long",
          right: "A right label that also runs long",
        },
        { left: "Short left", right: "Short right" },
      ],
    },
    {
      prompt: "Sample 5 — last one.",
      pairs: [
        { left: "Left A", right: "Right A" },
        { left: "Left B", right: "Right B" },
        { left: "Left C", right: "Right C" },
      ],
    },
  ],

  // CountPop treats questions[0] as configuration, not a question. Handing it
  // an answers-shaped array yields a silent default board, which reads as
  // "the game is broken" when it is really "the data was the wrong shape".
  countPop: [
    {
      maxNumber: 20,
      target: 7,
      prompt: "Sample — pop the balloons in order.",
      pointsPerCorrect: 5,
      wrongPenalty: 3,
    },
  ],

  // MatchThree, PuzzleShuffle and RhythmTap generate their own boards. They
  // still receive an array so a component reading questions[0] for a title,
  // a size or a background image does not hit undefined.
  generated: [
    {
      prompt: "Sample — this game builds its own board.",
      size: 3,
    },
  ],
};

// Label It! — one board on a drawn sample picture (sun, house, roof, tree).
SAMPLES.labelIt = [
  {
    question: "Sample — drag each word to its place on the picture.",
    image:
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
       <rect width="800" height="600" fill="#cfe8ff"/>
       <rect y="430" width="800" height="170" fill="#9fd67a"/>
       <circle cx="660" cy="110" r="62" fill="#ffd23f"/>
       <rect x="150" y="270" width="230" height="180" fill="#f4a261"/>
       <polygon points="130,280 265,160 400,280" fill="#c0392b"/>
       <rect x="245" y="350" width="50" height="100" fill="#7a4b2a"/>
       <rect x="560" y="300" width="34" height="150" fill="#8b5a2b"/>
       <circle cx="577" cy="270" r="78" fill="#2e9e4f"/>
     </svg>`
      ),
    imageAlt: "A house, a tree and the sun",
    tiles: [
      { id: "sun", text: "Sun", x: 82.5, y: 18.3 },
      { id: "roof", text: "Roof", x: 33, y: 36 },
      { id: "door", text: "Door", x: 33.7, y: 66 },
      { id: "tree", text: "Tree", x: 72, y: 45 },
      { id: "grass", text: "Grass", x: 12, y: 85 },
    ],
  },
];

export const SAMPLE_KEYS = Object.keys(SAMPLES);

export const SAMPLE_LABELS = {
  answers: "5 multiple-choice questions",
  options: "5 drag-and-drop sets",
  pairs: "5 matching sets",
  countPop: "1 settings object (read as config, not a question)",
  generated: "self-generated board",
  labelIt: "1 Label It! picture with 5 tiles",
};

/** Never returns undefined — a bad `sample` key falls back rather than crashing. */
export const sampleFor = (key) => SAMPLES[key] || SAMPLES.answers;

/* ------------------------------------------------------------------ */
/* Placeholder image                                                   */
/* ------------------------------------------------------------------ */

// Several games render placeholderImage as a card face, a puzzle source or a
// results background, and show an empty box without one. Inline SVG so the
// harness has no network dependency and no Storage read.
export const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="360" height="360">
       <rect width="360" height="360" fill="#e8eef7"/>
       <circle cx="120" cy="120" r="54" fill="#c3d3ee"/>
       <rect x="196" y="66" width="108" height="108" rx="16" fill="#aebfe0"/>
       <rect x="66" y="212" width="228" height="24" rx="12" fill="#c3d3ee"/>
       <rect x="66" y="252" width="150" height="24" rx="12" fill="#d3dff0"/>
       <text x="180" y="322" font-family="system-ui,sans-serif" font-size="17"
             fill="#5a6b85" text-anchor="middle">sample image</text>
     </svg>`
  );

/* ------------------------------------------------------------------ */
/* Main games                                                          */
/* ------------------------------------------------------------------ */

// `gameType` strings are what UniversalQuizGame's switch accepts after
// normalizeGameType() strips punctuation and applies GAME_TYPE_ALIASES. They
// are written here in the spelling a topic would actually carry, so a working
// entry in this list is a string that can be pasted straight into a topic.
export const MAIN_GAMES = [
  {
    id: "multipleChoice",
    label: "Multiple Choice",
    gameType: "multipleChoice",
    sample: "answers",
    load: () => import("../components/quizzes/MultipleChoice"),
    note:
      "Reads current.answers without guarding. Point it at drag-drop data and it throws — which is why reconcileGameType exists in UniversalQuizGame.",
  },
  {
    id: "dragDrop",
    label: "Drag and Drop",
    gameType: "dragDrop",
    sample: "options",
    load: () => import("../components/quizzes/DragDropQuiz"),
    note:
      'The topics files spell this "dragAndDrop" and the quiz content files spell it "dragDrop". The alias map folds both to dragdrop.',
  },
  {
    id: "matching",
    label: "Matching",
    gameType: "matching",
    sample: "pairs",
    load: () => import("../components/quizzes/MatchingQuiz"),
    note: "Reads current.pairs — a different shape from every other game here.",
  },
  {
    id: "match3",
    label: "Match Three",
    gameType: "match3",
    sample: "generated",
    load: () => import("../components/quizzes/MatchThreeGame"),
    note:
      "Generates its own 6x6 board. Starting-board solvability is a known open issue — a dead board on load is that bug, not this panel.",
  },
  {
    id: "memoryMatch",
    label: "Memory Match",
    gameType: "memoryMatch",
    sample: "options",
    load: () => import("../components/quizzes/MemoryMatchGame"),
    note: "Builds card pairs from options[].text and options[].targetText.",
  },
  {
    id: "countPop",
    label: "Count Pop",
    gameType: "countPop",
    sample: "countPop",
    load: () => import("../components/quizzes/CountPop"),
    note:
      "questions[0] is configuration, not a question. Its sound URLs are still unverified — silence here may be a 404, not a mute.",
  },
  {
    id: "talkChoice",
    label: "Talk Choice",
    gameType: "talkChoice",
    sample: "answers",
    load: () => import("../components/quizzes/TalkChoice"),
    note:
      "Uses the browser speechSynthesis voice. Known cosmetic score bug on the final question.",
  },
  {
    id: "sortingConveyor",
    label: "Sorting Conveyor",
    gameType: "sortingConveyor",
    sample: "options",
    load: () => import("../components/quizzes/SortingConveyor"),
    note:
      "Reads the same options + targetText as drag and drop, so every existing drag-drop topic works here with no authoring. Two taps instead of a drag, which is the input young hands and touchscreens actually manage.",
  },
  {
    id: "classQuizShow",
    label: "Class Quiz Show",
    gameType: "classQuizShow",
    sample: "answers",
    load: () => import("../components/quizzes/ClassQuizShow"),
    // Kept OUT of the topic dropdown on purpose, and not for the usual reason.
    // It runs fine — but it is a WHOLE-CLASS game, and UniversalQuizGame's
    // completion path writes a score to one signed-in student's progress
    // record. A team total is not a student's score, and pushing it there
    // would corrupt the per-student mirror that EducatorClassroom and
    // ClassroomLeaderboard both read. Setting this on a topic needs an
    // educator route that skips the progress write, not just a switch case.
    liveSupported: false,
    note:
      "Whole-class team game for the smartboard. Runs on ordinary multiple-choice data. Teams buzz with the Q, P, Z and M keys or by tapping their button; space opens the buzzers. Reads and writes no student data at all.",
  },
  {
    id: "typingQuiz",
    label: "Typing Quiz",
    gameType: "typingQuiz",
    sample: "answers",
    load: () => import("../components/quizzes/TypingQuiz"),
    // Playable in the Game library, but deliberately kept OUT of the topic
    // dropdown until UniversalQuizGame has a case for it. Offering a child a
    // game the player cannot render is worse than not offering it.
    liveSupported: false,
    note:
      "Playable here, but UniversalQuizGame has no case for typingQuiz — a topic set to it would show \"Unsupported quiz type\". Add the case before using it on a live topic.",
  },
  {
    id: "labelIt",
    label: "Label It!",
    gameType: "labelIt",
    sample: "labelIt",
    load: () => import("../components/quizzes/LabelItGame"),
    note:
      "Drag name tiles onto spots on a picture. Questions are BOARDS ({ question, image, tiles: [{ text, image, x, y }] }), played in order. Made in Layout Builder ▸ Label It! games.",
  },
];

// Main games reachable only through a "mode:value" gameType string. These are
// not in the switch at all — parseMathGameType intercepts them first, so they
// need their own picker rather than sitting in MAIN_GAMES.
export const MATH_GAMES = [
  {
    id: "mathTimesTable",
    label: "Times table",
    mode: "mathTimesTable",
    value: 5,
    sample: "generated",
  },
  {
    id: "mathAddition",
    label: "Addition",
    mode: "mathAddition",
    value: 10,
    sample: "generated",
  },
  {
    id: "mathSubtraction",
    label: "Subtraction",
    mode: "mathSubtraction",
    value: 10,
    sample: "generated",
  },
];

/* ------------------------------------------------------------------ */
/* Bonus rounds                                                        */
/* ------------------------------------------------------------------ */

// `bonusRoundType` strings, as BonusRouter normalises them.
//
// The diamondTap entries are the interesting ones: diamondTap is not a single
// game. It falls through to bonusRoundMap keyed on SLUG, so which component
// you get depends on the topic, and "default" is BonusDiamonds. The `slug`
// field is what drives that lookup, and listing the variants separately is
// the only way to preview them without building a topic.
export const BONUS_ROUNDS = [
  {
    id: "diamondTap",
    label: "Diamond Tap (default)",
    bonusRoundType: "diamondTap",
    slug: "__lab__",
    sample: "answers",
    note: "Any slug with no entry in bonusRoundMap lands here, on BonusDiamonds.",
  },
  {
    id: "diamondYellow",
    label: "Diamond Tap — yellow",
    bonusRoundType: "diamondTap",
    slug: "motion",
    sample: "answers",
    note: "Same bonusRoundType as above. Only the slug differs.",
  },
  {
    id: "sideScrollerPlane",
    label: "Side-scroller — plane",
    bonusRoundType: "diamondTap",
    slug: "matter",
    sample: "answers",
    note:
      "The only side-scroller any slug currently points at. Car, rocket and fish are built but unreachable — see UNREACHABLE.",
  },
  {
    id: "memoryMatchBonus",
    label: "Memory Match",
    bonusRoundType: "memoryMatch",
    sample: "options",
  },
  {
    id: "match3Bonus",
    label: "Match Three",
    bonusRoundType: "match3",
    sample: "generated",
  },
  {
    id: "countPopBonus",
    label: "Count Pop",
    bonusRoundType: "countPop",
    sample: "countPop",
  },
  {
    id: "typingQuizBonus",
    label: "Typing Quiz",
    bonusRoundType: "typingQuiz",
    sample: "answers",
  },
  {
    id: "rhythmTapBonus",
    label: "Rhythm Tap",
    bonusRoundType: "rhythmTap",
    sample: "generated",
  },
  {
    id: "puzzleShuffleBonus",
    label: "Puzzle Shuffle",
    bonusRoundType: "puzzleShuffle",
    sample: "generated",
  },
  {
    id: "treasureQuestBonus",
    label: "Treasure Quest (map + match 3)",
    bonusRoundType: "treasureQuest",
    sample: "generated",
    note:
      "The avatar walks a map to a treasure chest; every stop is a match-3 board with a set number of moves. Difficulty follows the student's grade; boards come from the student's quest level, so they're new every time.",
  },
  {
    id: "noneBonus",
    label: "None (skip to results)",
    bonusRoundType: "none",
    sample: "generated",
    note:
      "Completes immediately with a score of 0. Choosing this is how you test a game's hand-off without sitting through a bonus round.",
  },
];

// UniversalMathBonus is reached through "mode:value", same as the math games —
// but note the vocabulary differs from MATH_GAMES above. The bonus says
// timesTable, the main game says mathTimesTable. That is not a typo here; it
// is a real inconsistency between BonusRouter and parseMathGameType.
export const MATH_BONUS = [
  { id: "bonusTimesTable", label: "Times table", bonusRoundType: "timesTable:5", sample: "generated" },
  { id: "bonusCountBy", label: "Count by", bonusRoundType: "countBy:5", sample: "generated" },
  { id: "bonusAddition", label: "Addition", bonusRoundType: "addition:10", sample: "generated" },
  { id: "bonusSubtraction", label: "Subtraction", bonusRoundType: "subtraction:10", sample: "generated" },
];

// Built, renders fine, but no slug in bonusRoundMap points at it — so nothing
// in the app can currently reach it. Listed so this stays visible rather than
// being rediscovered in six months. To reach one, add a slug key to
// bonusRoundMap in BonusRouter.js and add an entry to BONUS_ROUNDS above.
export const UNREACHABLE = [
  { label: "Side-scroller — car", detail: "BonusCarRace, no slug in bonusRoundMap" },
  { label: "Side-scroller — rocket", detail: "BonusRocketRace, no slug in bonusRoundMap" },
  { label: "Side-scroller — fish", detail: "BonusFishWorld, no slug in bonusRoundMap" },
];

/* ------------------------------------------------------------------ */
/* Derived lists — what the Topic Library dropdowns read                */
/* ------------------------------------------------------------------ */

const uniq = (list) => Array.from(new Set(list.filter(Boolean)));

// Distinct gameType strings, for a topic's Game dropdown.
//
// Two filters, both deliberate. `liveSupported: false` drops anything this
// panel can play but UniversalQuizGame cannot yet render, so a topic can
// never be saved pointing at a game that shows a child "Unsupported quiz
// type". And uniq() because a catalogue entry is not a type — several bonus
// entries share the string "diamondTap", and a dropdown listing it three
// times is a bug report waiting to happen.
export const GAME_TYPE_VALUES = uniq(
  MAIN_GAMES.filter((g) => g.liveSupported !== false).map((g) => g.gameType)
);

// Distinct bonusRoundType strings, for a topic's Bonus dropdown. The
// slug-driven diamondTap variants collapse into the single "diamondTap"
// entry, which is correct: a topic picks the type, and its own slug picks
// the variant.
export const BONUS_TYPE_VALUES = uniq(
  BONUS_ROUNDS.map((b) => b.bonusRoundType)
);

/* ------------------------------------------------------------------ */
/* Lookups                                                             */
/* ------------------------------------------------------------------ */

// Content files disagree about how to spell a game type. The topics files say
// "dragAndDrop", the quiz content files say "dragDrop", and the switch in
// UniversalQuizGame only ever matched the second — so every topic authored
// with the first spelling fell through to "Unsupported quiz type".
//
// This is a copy of the map that currently lives inside UniversalQuizGame.js.
// It is here so the builder resolves a typed string exactly the way a student
// would. When UniversalQuizGame is next touched, it should import these two
// from this file and delete its own copies; until then, an alias added in one
// place must be added in the other.
export const GAME_TYPE_ALIASES = {
  draganddrop: "dragdrop",
  dragndrop: "dragdrop",
  drag: "dragdrop",
  mc: "multiplechoice",
  multiplechoicequiz: "multiplechoice",
  matchingquiz: "matching",
  memory: "memorymatch",
  memorymatchgame: "memorymatch",
  matchthreegame: "match3",
  countandpop: "countpop",
  labelthepicture: "labelit",
  label: "labelit",
};

export const normalizeGameType = (value = "") => {
  const base = String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  return GAME_TYPE_ALIASES[base] || base;
};

/** "mathTimesTable:5" -> { mode, value }. Anything else -> null. */
export const parseMathGameType = (gameType = "") => {
  const raw = String(gameType || "");
  if (!raw.includes(":")) return null;

  const [modeRaw, valueRaw] = raw.split(":");
  const mode = String(modeRaw || "").toLowerCase();
  const value = Number(valueRaw) || 5;

  if (["mathtimestable", "mathaddition", "mathsubtraction"].includes(mode)) {
    return { mode, value };
  }

  return null;
};

/**
 * A raw gameType string -> the catalogue entry a student would actually get.
 * Returns null when nothing matches, which is the case that renders
 * "Unsupported quiz type" in the real player.
 */
export const resolveGameType = (value) => {
  const target = normalizeGameType(value);
  if (!target) return null;

  return (
    MAIN_GAMES.find((g) => normalizeGameType(g.gameType) === target) || null
  );
};

export const findMainGame = (id) => MAIN_GAMES.find((g) => g.id === id) || null;
export const findMathGame = (id) => MATH_GAMES.find((g) => g.id === id) || null;
export const findBonus = (id) => BONUS_ROUNDS.find((b) => b.id === id) || null;
export const findMathBonus = (id) => MATH_BONUS.find((b) => b.id === id) || null;

/**
 * A completion payload can be a bare number, or { score }, or
 * { score, answers, details }, depending on which game fired it. Every caller
 * that wants a number has been re-deriving this; here it is once.
 */
export const scoreOf = (payload) => {
  if (typeof payload === "number") return payload;
  const value = Number(payload?.score);
  return Number.isFinite(value) ? value : 0;
};
